import { z } from "zod";
import { PositionEventZ } from "../webhooks/position";

/**
 * Frames the Playwright bot pushes to the Worker over the long-lived
 * WebSocket at /ws/bot. Discriminated by `type`. The Worker's BotChannel
 * Durable Object owns the connection (hibernation API) and routes each
 * frame to the right HedgePair DO based on `accountId`.
 *
 * Wire format:
 *   wss://api.pfh.trading/ws/bot?bot=<BOT_NAME>&ts=<unix_ms>&sig=<hex>
 *   sig = hex(HMAC-SHA256(EA_WEBHOOK_SECRET, ts + "." + bot))
 *
 * Multiplexing: ONE connection per bot instance, tagged per-account in the
 * frame body. Replaces the per-tick HTTP `postPosition` calls. The HTTP
 * path remains as a fallback when the WS has been closed > 30 s.
 */

export const BotHelloFrameZ = z.object({
  type: z.literal("bot:hello"),
  bot: z.string().min(1).max(64),
  accounts: z.array(z.string().min(1).max(32)),
  /**
   * Source commit SHA baked into the image at build time
   * (Dockerfile's ARG GIT_COMMIT → ENV PFH_WORKER_VERSION). Sent on
   * every reconnect so the dashboard can reflect the live bot version
   * without a separate heartbeat. Optional for back-compat with bots
   * built before this field existed.
   */
  version: z.string().max(64).optional(),
  /**
   * Hardware reporting for the manager-driven sleep/wake cap. The
   * server stores these on `bots` and computes max_concurrent_awake
   * from the formula in shared/src/bots/sleep-state.ts. Optional so
   * older bot images keep registering.
   */
  totalMemMb: z.number().int().nonnegative().optional(),
  freeMemMb: z.number().int().nonnegative().optional(),
  ts: z.number().int(),
});

export const BotByeFrameZ = z.object({
  type: z.literal("bot:bye"),
  bot: z.string().min(1).max(64),
  ts: z.number().int(),
});

/**
 * Lightweight equity tick — fired on every observable change to the
 * footer/status bar. Cheaper than a full PositionEvent and dashboard
 * animates these directly into the equity ticker.
 */
export const AccountTickFrameZ = z.object({
  type: z.literal("account:tick"),
  accountId: z.string().min(1).max(32),
  equity: z.number(),
  balance: z.number(),
  marginLevel: z.number().nullable(),
  openPnl: z.number(),
  /**
   * Per-tick aggregate position counts. Cheap for the bot to compute
   * (it already has the position list from `getOpenPositions()`) and
   * lets the server's sleep-state decision see live SL/TP coverage
   * without polling. Optional for backward compatibility — older
   * bot images that don't send these are treated as 0/0 by the server.
   */
  openPositions: z.number().int().nonnegative().optional(),
  protectedPositions: z.number().int().nonnegative().optional(),
  ts: z.number().int(),
});

/**
 * Full position snapshot — fired on open/close/modify observed in the DOM.
 * Reuses the canonical PositionEvent so HedgePair.onPositionUpdate can
 * consume it unchanged.
 */
export const PositionUpdateFrameZ = z.object({
  type: z.literal("position:update"),
  payload: PositionEventZ,
});

/**
 * Account info reported by `driver.verifyAccount()` after a successful
 * login. Read-only — no orders are placed during verification. Mirrors
 * the `AccountInfo` interface in adapters/playwright-bot/src/drivers/base.ts.
 */
export const AccountInfoZ = z.object({
  accountNumber: z.string(),
  server: z.string(),
  accountType: z.string(),
  broker: z.string(),
  currency: z.string(),
  balance: z.number(),
  deposit: z.number(),
  verifiedAt: z.number().int(),
});
export type AccountInfo = z.infer<typeof AccountInfoZ>;

/**
 * Validation result reported by a one-shot validation worker. Manager
 * forwards this over the WebSocket; BotChannel updates the D1 row and,
 * on success, fires the regular `account:assigned` push so the trading
 * worker spawns next.
 */
export const AccountValidationResultFrameZ = z.object({
  type: z.literal("account:validation-result"),
  accountId: z.string().min(1).max(32),
  ok: z.boolean(),
  info: AccountInfoZ.nullable(),
  error: z.string().max(1024).nullable(),
  ts: z.number().int(),
});

export const AccountOfflineFrameZ = z.object({
  type: z.literal("account:offline"),
  accountId: z.string().min(1).max(32),
  reason: z.string().max(256),
  ts: z.number().int(),
});

/**
 * Bot → server ack for the manager-driven sleep/wake feature. Emitted
 * whenever the supervisor actually transitions a worker (Brave closed
 * or relaunched), so the dashboard can show ground truth instead of
 * just operator intent. The server forwards this into the per-pair
 * HedgePair event stream as `account:sleep-state`.
 */
export const AccountSleepStateAckFrameZ = z.object({
  type: z.literal("account:sleep-state"),
  accountId: z.string().min(1).max(32),
  /** "orphan" = supervisor tried to close Brave but the worker process
   *  did not actually exit within the drain deadline. Distinct from
   *  "asleep" so the dashboard can surface a retry / force-kill UI
   *  instead of showing fake-asleep state. */
  state: z.enum(["awake", "asleep", "orphan"]),
  reason: z.string().max(64),
  ts: z.number().int(),
});

/**
 * Captured by the worker just before context.close() runs on an
 * operator-driven sleep. Lets the dashboard show "Asleep since X with
 * these positions" instead of going dark — the live tick stream is
 * normally only WS-broadcast (not persisted) and a sleeping account
 * has no live tick at all. The bot has live page access here, so it
 * can read the broker's footer + positions table one last time.
 */
export const AccountPreSleepSnapshotFrameZ = z.object({
  type: z.literal("account:pre-sleep-snapshot"),
  accountId: z.string().min(1).max(32),
  /** Free-form reason string the worker received on /close (e.g.
   *  "kill", "drain complete", "SIGTERM"). The original AccountSleep
   *  CmdFrame.reason ("manual"|"idle"|"cap"|"weekend") would be more
   *  accurate but isn't currently plumbed past supervisor.sleepAccount;
   *  free-form keeps the snapshot useful without requiring that change.
   *  The dashboard only renders the timestamp + position summary, not
   *  the reason verbatim. */
  reason: z.string().max(64),
  snapshot: z.object({
    equity: z.number().nullable(),
    balance: z.number().nullable(),
    marginLevel: z.number().nullable(),
    openPnl: z.number(),
    closedPnl: z.number().nullable(),
    drawdownUsed: z.number().nullable(),
    dailyUsed: z.number().nullable(),
    openPositions: z.array(
      z.object({
        ticket: z.number().int(),
        symbol: z.string().max(16),
        side: z.enum(["buy", "sell"]),
        lots: z.number().nonnegative(),
        entry: z.number(),
        stopLoss: z.number().nullable(),
        takeProfit: z.number().nullable(),
        openPnl: z.number(),
      }),
    ),
    positionCount: z.number().int().nonnegative(),
    protectedCount: z.number().int().nonnegative(),
    capturedAt: z.number().int(),
  }),
  ts: z.number().int(),
});

// ── 3-stage handshake: bot → worker acks ─────────────────────────
//
// Each stage of the prepare/stage/go protocol emits one ack frame per
// leg. BotChannel forwards to the owning HedgePair DO, which advances
// the pending_trade state machine. `tradeId` threads through all four
// frames to pair them.

export const TradePreparedFrameZ = z.object({
  type: z.literal("trade:prepared"),
  pairId: z.string().min(1).max(64),
  tradeId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ok: z.boolean(),
  bid: z.number().nullable(),
  ask: z.number().nullable(),
  /**
   * Max lots this leg can sustain RIGHT NOW given live freeMargin +
   * leverage + contract size. HedgePair clamps stage lots to
   * min(target, long.sustainable, short.sustainable) so the leg in
   * drawdown auto-sizes down instead of getting "not enough margin".
   * Null when the bot couldn't compute (missing leverage / contract
   * size / price) — HedgePair then falls back to the target.
   */
  sustainableLots: z.number().positive().nullable().optional(),
  error: z.string().max(512).nullable(),
  ts: z.number().int(),
});

export const TradeStagedFrameZ = z.object({
  type: z.literal("trade:staged"),
  pairId: z.string().min(1).max(64),
  tradeId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ok: z.boolean(),
  error: z.string().max(512).nullable(),
  ts: z.number().int(),
});

export const TradeOpenedFrameZ = z.object({
  type: z.literal("trade:opened"),
  pairId: z.string().min(1).max(64),
  tradeId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ok: z.boolean(),
  ticket: z.number().int().nullable(),
  fillPrice: z.number().nullable(),
  error: z.string().max(512).nullable(),
  ts: z.number().int(),
});

export const TradeAbortedFrameZ = z.object({
  type: z.literal("trade:aborted"),
  pairId: z.string().min(1).max(64),
  tradeId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  reason: z.string().max(256),
  ts: z.number().int(),
});

/**
 * News-protect ack. Bot has computed the new SL price + expected loss
 * and is asking the worker for a sanity-check Haiku call before the
 * modify is actually fired at the broker. Worker replies via the
 * existing trade:news_protect outbound frame with `approved`.
 */
export const TradeNewsProtectAppliedFrameZ = z.object({
  type: z.literal("trade:news_protect_applied"),
  pairId: z.string().min(1).max(64).nullable().optional(),
  accountId: z.string().min(1).max(32),
  ticket: z.number().int().nullable(),
  newSl: z.number().nullable(),
  expectedLossDollars: z.number().nonnegative(),
  ok: z.boolean(),
  error: z.string().max(512).nullable(),
  ts: z.number().int(),
});

/** Weekend status-check one-shot. Bot logs in, reads snapshot, sends
 *  this frame, closes browser. No position data — dashboard uses this
 *  to show "last alive at X" without running a full worker. */
export const TradeStatusCheckCompleteFrameZ = z.object({
  type: z.literal("trade:status_check_complete"),
  pairId: z.string().min(1).max(64).nullable().optional(),
  accountId: z.string().min(1).max(32),
  snapshot: z.object({
    equity: z.number().nullable(),
    balance: z.number().nullable(),
    openPositions: z.number().int().nonnegative(),
  }),
  ts: z.number().int(),
});

/**
 * Reply to a `symbol:validate` request. Every numeric field is nullable
 * — drivers that can't reach a particular DOM element return `null`
 * rather than failing the whole check. `ok=false` means "the symbol is
 * not tradable on this broker at all" (e.g. search returns no results).
 */
export const SymbolValidateResultFrameZ = z.object({
  type: z.literal("symbol:validate-result"),
  requestId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  symbol: z.string().min(1).max(16),
  ok: z.boolean(),
  resolvedSymbol: z.string().nullable(),
  leverage: z.number().nullable(),
  contractSize: z.number().nullable(),
  minLot: z.number().nullable(),
  maxLot: z.number().nullable(),
  spreadPips: z.number().nullable(),
  error: z.string().nullable(),
  ts: z.number().int(),
});

/**
 * Reply to a `command:open` / `command:close` / `command:modify` request.
 * Mirrors the `symbol:validate-result` round-trip: bot worker emits one
 * of these after `executeCommand` returns, BotChannel UPDATEs the
 * matching `manual_commands` row by `requestId`, and the waiting HTTP
 * handler in the manual-trade route returns the real per-leg outcome.
 */
export const CommandResultFrameZ = z.object({
  type: z.literal("command:result"),
  requestId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  kind: z.enum(["open", "close", "modify"]),
  ok: z.boolean(),
  /** Broker-assigned ticket on `open`, the closed/modified ticket on
   *  `close`/`modify`. Null when the driver couldn't surface one (e.g.
   *  open() failed before reaching the broker). */
  ticket: z.number().int().nullable(),
  /** Open only: the price the driver observed for the fill. Null when
   *  the driver doesn't report it. */
  fillPrice: z.number().nullable().optional(),
  error: z.string().max(1024).nullable(),
  ts: z.number().int(),
});

/**
 * Reply to a `scrape:pfm` request. Returns a markdown render of a
 * propfirmmatch.com page. Bot-agnostic (no accountId) — any online bot
 * can service the request. Worker correlates via `requestId` against
 * the `pfm_scrapes` D1 row.
 */
export const ScrapePfmResultFrameZ = z.object({
  type: z.literal("scrape:pfm:result"),
  requestId: z.string().min(1).max(64),
  ok: z.boolean(),
  markdown: z.string().nullable(),
  finalUrl: z.string().nullable(),
  error: z.string().max(1024).nullable(),
  ts: z.number().int(),
});

/**
 * One imported trade returned by a manual history-refetch. Shape maps
 * directly to `driver.getTradeHistory`'s HistoryTrade. Non-trade rows
 * ("balance", deposits) MUST be filtered out before the frame leaves
 * the bot — only buy/sell entries belong here. Times are ms epoch
 * (bot parses the platform's human-readable string before sending).
 */
export const HistoryTradeZ = z.object({
  ticket: z.number().int(),
  side: z.enum(["buy", "sell"]),
  symbol: z.string().max(32),
  lots: z.number(),
  entryPrice: z.number(),
  closePrice: z.number(),
  openedAt: z.number().int(),
  closedAt: z.number().int(),
  pnl: z.number(),
  commission: z.number(),
  fee: z.number(),
  swap: z.number(),
  comment: z.string().max(256),
});
export type HistoryTrade = z.infer<typeof HistoryTradeZ>;

/**
 * Reply to `account:history:fetch`. Bot scraped the platform's History
 * tab via driver.getTradeHistory and returned every closed trade it
 * could see. Worker upserts these into D1 `trades` so a freshly-added
 * or drained account gains backfill without waiting for new fills.
 */
export const AccountHistoryResultFrameZ = z.object({
  type: z.literal("account:history:result"),
  requestId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ok: z.boolean(),
  trades: z.array(HistoryTradeZ).max(2000),
  error: z.string().max(1024).nullable(),
  ts: z.number().int(),
});
export type AccountHistoryResultFrame = z.infer<typeof AccountHistoryResultFrameZ>;

export const BotFrameZ = z.discriminatedUnion("type", [
  BotHelloFrameZ,
  BotByeFrameZ,
  AccountTickFrameZ,
  PositionUpdateFrameZ,
  AccountOfflineFrameZ,
  AccountSleepStateAckFrameZ,
  AccountPreSleepSnapshotFrameZ,
  AccountValidationResultFrameZ,
  TradePreparedFrameZ,
  TradeStagedFrameZ,
  TradeOpenedFrameZ,
  TradeAbortedFrameZ,
  TradeNewsProtectAppliedFrameZ,
  TradeStatusCheckCompleteFrameZ,
  SymbolValidateResultFrameZ,
  CommandResultFrameZ,
  ScrapePfmResultFrameZ,
  AccountHistoryResultFrameZ,
]);
export type BotFrame = z.infer<typeof BotFrameZ>;
export type BotHelloFrame = z.infer<typeof BotHelloFrameZ>;
export type BotByeFrame = z.infer<typeof BotByeFrameZ>;
export type AccountTickFrame = z.infer<typeof AccountTickFrameZ>;
export type PositionUpdateFrame = z.infer<typeof PositionUpdateFrameZ>;
export type AccountOfflineFrame = z.infer<typeof AccountOfflineFrameZ>;
export type AccountSleepStateAckFrame = z.infer<typeof AccountSleepStateAckFrameZ>;
export type AccountPreSleepSnapshotFrame = z.infer<typeof AccountPreSleepSnapshotFrameZ>;
export type AccountValidationResultFrame = z.infer<typeof AccountValidationResultFrameZ>;
export type TradePreparedFrame = z.infer<typeof TradePreparedFrameZ>;
export type TradeStagedFrame = z.infer<typeof TradeStagedFrameZ>;
export type TradeOpenedFrame = z.infer<typeof TradeOpenedFrameZ>;
export type TradeAbortedFrame = z.infer<typeof TradeAbortedFrameZ>;
export type TradeNewsProtectAppliedFrame = z.infer<typeof TradeNewsProtectAppliedFrameZ>;
export type TradeStatusCheckCompleteFrame = z.infer<typeof TradeStatusCheckCompleteFrameZ>;
export type SymbolValidateResultFrame = z.infer<typeof SymbolValidateResultFrameZ>;
export type CommandResultFrame = z.infer<typeof CommandResultFrameZ>;
export type ScrapePfmResultFrame = z.infer<typeof ScrapePfmResultFrameZ>;

// ─── Inbound (server → manager) ───────────────────────────────────
//
// Pushed down the same WebSocket the manager opens for telemetry. The
// 5-min /sync poll is now a fallback consistency check; these frames
// are the hot path for assignment/removal/revocation events.

import { BotAccountRowZ } from "../api/bots";

export const AccountAssignedFrameZ = z.object({
  type: z.literal("account:assigned"),
  account: BotAccountRowZ,
  ts: z.number().int(),
});

export const AccountRemovedFrameZ = z.object({
  type: z.literal("account:removed"),
  accountId: z.string().min(1).max(32),
  reason: z.string().max(256),
  ts: z.number().int(),
});

export const BotRevokedFrameZ = z.object({
  type: z.literal("bot:revoked"),
  reason: z.string().max(256),
  ts: z.number().int(),
});

/**
 * Trigger an ephemeral validation worker. Spawned by the manager with
 * `VALIDATION_ONLY=true`; worker logs in + reads info + emits
 * `account:validation-result` + exits 0. Does NOT trade. Does NOT join
 * the long-lived worker pool.
 */
export const AccountValidateFrameZ = z.object({
  type: z.literal("account:validate"),
  account: BotAccountRowZ,
  ts: z.number().int(),
});

export const SymbolsPrepareFrameZ = z.object({
  type: z.literal("symbols:prepare"),
  accountId: z.string().min(1).max(32),
  symbols: z.array(z.string().min(1).max(32)).default([]),
  ts: z.number().int(),
});

// ── Worker → bot trade handshake commands ────────────────────────
//
// Pushed to the single bot that owns the target account. The bot
// dispatches to the account's per-account worker, which executes the
// corresponding driver method and replies with the matching trade:*
// ack frame.

export const TradePrepareFrameZ = z.object({
  type: z.literal("trade:prepare"),
  pairId: z.string().min(1).max(64),
  tradeId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  symbol: z.string().min(1).max(16),
  /** Contract size (units per lot) captured at symbol-validate time.
   *  Bot needs it to compute sustainable lots. Null-safe — bot falls
   *  back to target when either spec is unknown. */
  contractSize: z.number().positive().nullable().optional(),
  /** Account leverage ratio (e.g. 100 for 1:100). Captured at
   *  symbol-validate time. */
  leverage: z.number().positive().nullable().optional(),
  /** Operator's desired lots — the upper bound. Bot returns this back
   *  as sustainableLots when it can't compute a lower sustainable from
   *  margin, so HedgePair's clamp still works. */
  targetLots: z.number().positive().nullable().optional(),
  /** True when this prepare originated from an explicit operator click
   *  (POST /api/manual-trade/open-hedge). The supervisor's
   *  dormant-account drop bypasses operator-initiated frames so the
   *  account wakes for the click. Haiku/workflow paths leave this
   *  unset and remain subject to the drop. */
  operatorInitiated: z.boolean().optional(),
  ts: z.number().int(),
});

export const TradeStageFrameZ = z.object({
  type: z.literal("trade:stage"),
  pairId: z.string().min(1).max(64),
  tradeId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  side: z.enum(["buy", "sell"]),
  lots: z.number().positive(),
  slPrice: z.number().positive(),
  tpPrice: z.number().positive(),
  ts: z.number().int(),
});

export const TradeGoFrameZ = z.object({
  type: z.literal("trade:go"),
  pairId: z.string().min(1).max(64),
  tradeId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  side: z.enum(["buy", "sell"]),
  ts: z.number().int(),
});

export const TradeAbortCmdFrameZ = z.object({
  type: z.literal("trade:abort"),
  pairId: z.string().min(1).max(64),
  tradeId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  reason: z.string().max(256),
  ts: z.number().int(),
});

export const TradeNewsProtectCmdFrameZ = z.object({
  type: z.literal("trade:news_protect"),
  pairId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ticket: z.number().int().nullable(),
  maxLossDollars: z.number().positive(),
  removeTp: z.boolean().default(true),
  /** Set true on the second-pass worker→bot frame after Haiku's
   *  sanity check approves the bot's proposed SL. */
  approved: z.boolean().optional(),
  ts: z.number().int(),
});

export const TradeNewsRestoreCmdFrameZ = z.object({
  type: z.literal("trade:news_restore"),
  pairId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ticket: z.number().int().nullable(),
  newSlPrice: z.number().positive(),
  newTpPrice: z.number().positive(),
  ts: z.number().int(),
});

export const TradeForceCloseAllCmdFrameZ = z.object({
  type: z.literal("trade:force_close_all"),
  pairId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  // Symbol scope. Each pair maps to one symbol; the bot must close ONLY
  // matching-symbol tickets so sibling-pair positions on the same broker
  // login (e.g. XAUUSD when this pair is EURUSD) are left untouched.
  // Optional for one-revision rolling-update safety: if missing, the bot
  // falls back to closing every open ticket on the account.
  symbol: z.string().min(1).max(32).optional(),
  reason: z.string().max(256),
  closeDelayMs: z.number().int().min(0).max(5 * 60 * 1000).optional(),
  offlineUntilMs: z.number().int().optional(),
  ts: z.number().int(),
});

export const TradeStatusCheckCmdFrameZ = z.object({
  type: z.literal("trade:status_check"),
  pairId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ts: z.number().int(),
});

/**
 * Direct single-account execution commands. Used for operator-initiated
 * manual controls from the dashboard. The AI hedge flow still uses the
 * staged trade:* handshake so both legs stay mirrored.
 *
 * Every command carries a `requestId` correlator. The bot's reply rides
 * on `command:result` and the worker's manual-trade route polls
 * `manual_commands` (D1) until the matching row resolves — same shape
 * as the symbol-validation round-trip.
 */
export const CommandOpenCmdFrameZ = z.object({
  type: z.literal("command:open"),
  requestId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  symbol: z.string().min(1).max(16),
  side: z.enum(["buy", "sell"]),
  lots: z.number().positive(),
  stopLoss: z.number().nullable().optional(),
  takeProfit: z.number().nullable().optional(),
  comment: z.string().max(128).nullable().optional(),
  ts: z.number().int(),
});

export const CommandCloseCmdFrameZ = z.object({
  type: z.literal("command:close"),
  requestId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ticket: z.union([z.number().int(), z.literal("all")]),
  ts: z.number().int(),
});

export const CommandModifyCmdFrameZ = z.object({
  type: z.literal("command:modify"),
  requestId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ticket: z.number().int(),
  stopLoss: z.number().nullable(),
  takeProfit: z.number().nullable(),
  ts: z.number().int(),
});

/**
 * Symbol validation round-trip. Dashboard → Worker → Bot (inbound frame)
 * asking the driver to confirm a symbol is tradable on the broker and
 * report its key specs (leverage / contract size / min & max lot / spread).
 * Bot replies with `symbol:validate-result` keyed by the same requestId.
 *
 * Used when the operator adds a symbol to an account_pair's allowlist —
 * both legs are validated in parallel and the results surfaced in the
 * Add-Hedge Sheet. Validation failure on either leg blocks submit.
 */
export const SymbolValidateCmdFrameZ = z.object({
  type: z.literal("symbol:validate"),
  requestId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  symbol: z.string().min(1).max(16),
  ts: z.number().int(),
});

/**
 * Scrape propfirmmatch.com (or any URL) via the bot's stealth-playwright
 * instead of Cloudflare Browser Rendering — PFM's WAF blocks CF-BR's
 * known bot-detection ID as of 2026-03-17. Bot-agnostic: any online bot
 * services. The scrape runs in a separate singleton Chromium context
 * (not a trading profile) so the fingerprint doesn't leak between
 * trader personas and aggregator scrapes.
 */
export const ScrapePfmCmdFrameZ = z.object({
  type: z.literal("scrape:pfm"),
  requestId: z.string().min(1).max(64),
  url: z.string().url(),
  waitUntil: z
    .enum(["load", "domcontentloaded", "networkidle"])
    .default("networkidle"),
  timeoutMs: z.number().int().min(5_000).max(120_000).default(60_000),
  ts: z.number().int(),
});

/**
 * Operator-initiated backfill: "go scrape the platform's History tab
 * for this account and return every closed trade". Routed by the
 * manager to the per-account worker via stdin — the running browser
 * session is reused, no respawn. Reply rides on `account:history:result`.
 */
export const AccountHistoryFetchFrameZ = z.object({
  type: z.literal("account:history:fetch"),
  requestId: z.string().min(1).max(64),
  accountId: z.string().min(1).max(32),
  ts: z.number().int(),
});
export type AccountHistoryFetchFrame = z.infer<typeof AccountHistoryFetchFrameZ>;

/**
 * Manager-driven sleep/wake commands. Replaces the env-var-only
 * PFH_SESSION_CYCLING with a first-class feature. Server picks the
 * desired state from the rules in `shared/src/bots/sleep-state.ts`
 * and pushes one of these frames; supervisor closes/opens the per-
 * account Brave instance and replies via `account:sleep-state` ack
 * so the dashboard can reflect ground truth.
 */
export const AccountSleepCmdFrameZ = z.object({
  type: z.literal("account:sleep"),
  accountId: z.string().min(1).max(32),
  /** Why this transition was requested — surfaced in bot logs and the
   *  dashboard activity feed. */
  reason: z.enum(["manual", "idle", "cap", "weekend"]),
  ts: z.number().int(),
});

export const AccountWakeCmdFrameZ = z.object({
  type: z.literal("account:wake"),
  accountId: z.string().min(1).max(32),
  reason: z.enum(["manual", "presence", "safety", "news", "trade"]),
  ts: z.number().int(),
});

/**
 * Operator escalation when a normal sleep left a Brave instance orphan.
 * Bypasses the drain entirely: supervisor goes straight to /close →
 * SIGTERM → SIGKILL on the worker process, which kills the Chromium tree.
 */
export const AccountForceKillCmdFrameZ = z.object({
  type: z.literal("account:force-kill"),
  accountId: z.string().min(1).max(32),
  ts: z.number().int(),
});

/**
 * Self-update nudge from the worker. Fired after a new image is published
 * via GitHub Actions (/webhooks/ci/bot-image-published → BotChannel fan-out).
 * Bot handler forwards to Watchtower's HTTP API on localhost — Watchtower
 * does the docker pull + SIGTERM + recreate with graceful drain.
 *
 * `force=true` is reserved for future use (bypass drain on critical
 * security patches); current handler honours the regular drain path.
 */
export const BotUpdateFrameZ = z.object({
  type: z.literal("bot:update"),
  image_tag: z.string().min(1).max(128),
  image_digest: z.string().max(128).nullable().optional(),
  actor: z.string().max(64).nullable().optional(),
  force: z.boolean().default(false),
  ts: z.number().int(),
});
export type BotUpdateFrame = z.infer<typeof BotUpdateFrameZ>;

export const BotInboundFrameZ = z.discriminatedUnion("type", [
  AccountAssignedFrameZ,
  AccountRemovedFrameZ,
  AccountSleepCmdFrameZ,
  AccountWakeCmdFrameZ,
  AccountForceKillCmdFrameZ,
  BotRevokedFrameZ,
  AccountValidateFrameZ,
  SymbolsPrepareFrameZ,
  TradePrepareFrameZ,
  TradeStageFrameZ,
  TradeGoFrameZ,
  TradeAbortCmdFrameZ,
  TradeNewsProtectCmdFrameZ,
  TradeNewsRestoreCmdFrameZ,
  TradeForceCloseAllCmdFrameZ,
  TradeStatusCheckCmdFrameZ,
  CommandOpenCmdFrameZ,
  CommandCloseCmdFrameZ,
  CommandModifyCmdFrameZ,
  SymbolValidateCmdFrameZ,
  ScrapePfmCmdFrameZ,
  AccountHistoryFetchFrameZ,
  BotUpdateFrameZ,
]);
export type BotInboundFrame = z.infer<typeof BotInboundFrameZ>;
export type AccountAssignedFrame = z.infer<typeof AccountAssignedFrameZ>;
export type AccountRemovedFrame = z.infer<typeof AccountRemovedFrameZ>;
export type AccountSleepCmdFrame = z.infer<typeof AccountSleepCmdFrameZ>;
export type AccountWakeCmdFrame = z.infer<typeof AccountWakeCmdFrameZ>;
export type AccountForceKillCmdFrame = z.infer<typeof AccountForceKillCmdFrameZ>;
export type BotRevokedFrame = z.infer<typeof BotRevokedFrameZ>;
export type AccountValidateFrame = z.infer<typeof AccountValidateFrameZ>;
export type SymbolsPrepareFrame = z.infer<typeof SymbolsPrepareFrameZ>;
export type TradePrepareFrame = z.infer<typeof TradePrepareFrameZ>;
export type TradeStageFrame = z.infer<typeof TradeStageFrameZ>;
export type TradeGoFrame = z.infer<typeof TradeGoFrameZ>;
export type TradeAbortCmdFrame = z.infer<typeof TradeAbortCmdFrameZ>;
export type TradeNewsProtectCmdFrame = z.infer<typeof TradeNewsProtectCmdFrameZ>;
export type TradeNewsRestoreCmdFrame = z.infer<typeof TradeNewsRestoreCmdFrameZ>;
export type TradeForceCloseAllCmdFrame = z.infer<typeof TradeForceCloseAllCmdFrameZ>;
export type TradeStatusCheckCmdFrame = z.infer<typeof TradeStatusCheckCmdFrameZ>;
export type CommandOpenCmdFrame = z.infer<typeof CommandOpenCmdFrameZ>;
export type CommandCloseCmdFrame = z.infer<typeof CommandCloseCmdFrameZ>;
export type CommandModifyCmdFrame = z.infer<typeof CommandModifyCmdFrameZ>;
export type SymbolValidateCmdFrame = z.infer<typeof SymbolValidateCmdFrameZ>;
export type ScrapePfmCmdFrame = z.infer<typeof ScrapePfmCmdFrameZ>;
